import { FC, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useAtom, useSetAtom } from "jotai";
import { useGetProductsGroup } from "features/ProductsGroup/api";
import { transformItemsGroup } from "features/ProductsGroup/helpers";
import { ProductsGroupItem } from "features/ProductsGroup/components";
import { getItemsLeftAmountRequest } from "features/Documents/api/requests";

import { Button, EButtonVariant } from "components/Button/Button";
import { DialogContent } from "components/DialogContent/DialogContent";
import { VerticalSpace } from "components/VerticalSpace/VerticalSpace";
import { HorizontalSpace } from "components/HorizontalSpace/HorizontalSpace";
import { Option } from "components/Select/Select";
import RadioButton from "components/Radio/Radio";
import { MediumText } from "components/MediumText/MediumText";
import { Accordion } from "components/Accordion/Accordion";
import { Checkbox } from "components/Checkbox/Checkbox";
import { RegularText } from "components/RegularText/RegularText";

import {
  modal2Atom,
  setInfoModalAtom,
  setModal2CloseAtom,
  setModalCloseAtom,
} from "../../atoms/modalAtom/modalAtom";
import { ItemsGroupWithChildren } from "../../types";
import { openedDocumentAtom } from "../../atoms/documentsAtom";
import useDocuments from "../../hooks/useDocuments";

import styles from "./styles.module.css";

export type ItemGroupsSelectModalProps = {};

export const ItemGroupsSelectModal: FC<
  ItemGroupsSelectModalProps
> = (): JSX.Element => {
  const { data = [] } = useGetProductsGroup({});

  const transformedItemGroups: ItemsGroupWithChildren[] = transformItemsGroup([
    ...data,
  ]);

  const { createManyDocItems } = useDocuments();
  const closeModal = useSetAtom(setModal2CloseAtom);
  const setInfoModal = useSetAtom(setInfoModalAtom);
  const [openedDocument] = useAtom(openedDocumentAtom);
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [expandedAccordions, setExpandedAccordions] = useState<Set<string>>(
    new Set()
  );
  const [isFilling, setIsFilling] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        window.clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
    };
  }, []);

  const handleClick = async () => {
    if (isFilling) return;

    setIsFilling(true);
    setProgress(0);

    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }

    // Псевдо-прогресс пока ждём ответ.
    // Калибровка: 132 группы ≈ 16 секунд.
    const msPerGroup = 16000 / 132;
    const estimatedMs = Math.max(
      1500,
      Math.min(60000, selectedValues.length * msPerGroup)
    );
    const intervalMs = 120;
    const step = 90 / (estimatedMs / intervalMs);

    progressTimerRef.current = window.setInterval(() => {
      setProgress((p) => (p < 90 ? Math.min(90, p + step) : p));
    }, intervalMs);

    const data = await getItemsLeftAmountRequest({
      itemGroupIds: selectedValues,
      docId: openedDocument.docid,
    });

    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    setProgress(100);

    if (data && data.length) {
      createManyDocItems(data);

      setInfoModal({
        atomToOpen: modal2Atom,
        setAtomClose: setModal2CloseAtom,
        title: `Товары (${data.length}) были успешно добавлены в документ`,
      });

      setIsFilling(false);
      return;
    }

    setIsFilling(false);
    closeModal();
  };

  const toggleAccordion = (itemgroupid: string) => {
    setExpandedAccordions((prevState) => {
      const newExpanded = new Set(prevState);
      if (newExpanded.has(itemgroupid)) {
        newExpanded.delete(itemgroupid);
      } else {
        newExpanded.add(itemgroupid);
      }
      return newExpanded;
    });
  };

  const selectAll = () => {
    setSelectedValues(data.map((i) => i.itemgroupid));
  };

  const removeAll = () => {
    setSelectedValues([]);
  };

  const findGroupsAllChildrenRecursive = (groupIds: string[] = []) => {
    const firstChildrenIds = data
      .filter((i) => groupIds.includes(i.parentid))
      .map((i) => i.itemgroupid);

    if (!firstChildrenIds.length) {
      return [];
    }

    return [
      ...firstChildrenIds,
      ...findGroupsAllChildrenRecursive(firstChildrenIds),
    ];
  };

  const handleSelect = (groupId: string) => {
    let newSelectedValues = [...selectedValues];
    if (selectedValues.includes(groupId)) {
      newSelectedValues = selectedValues.filter((i) => i !== groupId);
    } else {
      const allChildrenIds = findGroupsAllChildrenRecursive([groupId]);
      newSelectedValues.push(groupId, ...allChildrenIds);
      newSelectedValues = [...new Set(newSelectedValues)];
    }
    setSelectedValues(newSelectedValues);
  };

  const renderItems = (items: ItemsGroupWithChildren[]) =>
    items.map((item) => {
      const { children } = item;

      if (children && children.length > 0) {
        return (
          <Accordion
            key={item.itemgroupid}
            withIcon
            className={styles.checkBoxRow}
            childrenClassName={styles.checkBoxRow}
            collapsed={!expandedAccordions.has(item.itemgroupid || "")}
            parent={
              <div
                onClick={() => {
                  toggleAccordion(item.itemgroupid || "");
                }}
                className={styles.checkBox}
                key={item.itemgroupid}
              >
                <div
                  onClick={(ev) => {
                    ev.stopPropagation();
                    handleSelect(item.itemgroupid);
                  }}
                >
                  <Checkbox
                    onClick={(ev) => {
                      ev.preventDefault();
                    }}
                    checked={selectedValues.includes(item.itemgroupid)}
                  />
                </div>
                <RegularText>{item.name}</RegularText>
              </div>
            }
          >
            <div className="pl-5">{renderItems(children)}</div>
          </Accordion>
        );
      }

      return (
        <div className={styles.checkBox} key={item.itemgroupid}>
          <div
            onClick={(ev) => {
              ev.stopPropagation();
              handleSelect(item.itemgroupid);
            }}
          >
            <Checkbox
              onClick={(ev) => {
                ev.preventDefault();
              }}
              checked={selectedValues.includes(item.itemgroupid)}
            />
          </div>
          <RegularText>{item.name}</RegularText>
        </div>
      );
    });

  return (
    <DialogContent
      className={classNames(styles.dialog, "flex items-center flex-col" as any)}
      setAtomClose={setModal2CloseAtom}
    >
      <MediumText
        size={16}
        className={classNames(
          styles.title,
          "mb-6 flex justify-center items-center text-center" as any
        )}
      >
        {"Выберите группы товаров"}
        <HorizontalSpace size={30} />
      </MediumText>
      {isFilling && (
        <div className={styles.progressWrap}>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
      <VerticalSpace size={12} />
      <div className={styles.buttonsRow}>
        <Button
          onClick={selectAll}
          disabled={isFilling || selectedValues.length === data.length}
        >
          Выбрать все
        </Button>
        <Button
          onClick={removeAll}
          disabled={isFilling || selectedValues.length === 0}
        >
          Снять все
        </Button>
      </div>
      <div className={styles.ratioList}>
        {renderItems(transformedItemGroups)}
      </div>
      <VerticalSpace size={36} />
      <div className="flex flex-row mt-auto">
        <Button
          disabled={isFilling || !selectedValues?.length}
          onClick={handleClick}
          variant={EButtonVariant.SECONDARY}
        >
          {"Выбрать"}
        </Button>
        <HorizontalSpace size={10} />
        <Button
          onClick={closeModal}
          variant={EButtonVariant.TERTIARY}
          disabled={isFilling}
        >
          Отмена
        </Button>
      </div>
    </DialogContent>
  );
};
